import { TextField, Typography, Link, Box, Button, Slider, useTheme, Divider, Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, List, ListItem, ListItemText } from "@material-ui/core"
import copy from 'copy-to-clipboard';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import ShareIcon from '@material-ui/icons/Share';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import clsx from "clsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import ChordSelector, { Chord } from "./components/ChordSelector"
import ModeSelector from "./components/ModeSelector"
import Scale from "./components/Scale"
import TonicSelector from "./components/TonicSelector"
import 'firebaseui/dist/firebaseui.css'
import { Mode, Note, midiToNote, getMidiChord, getChordName } from "./util/keys"
import Auth, { useAuth } from "./components/Auth";
import useMusic from "./hooks/useMusic";
import { firestore } from "./firebase";
import { useSnackbar } from "notistack";

export type Props = {
}

const useStyles = makeStyles((theme: Theme) => createStyles({
    text: {
        maxWidth: theme.breakpoints.values.lg,
        [theme.breakpoints.down('lg')]: {
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
        },
        margin: '0 auto'
    },
    chordSelector: {
        display: 'inline-flex',
    },
    leftMenu: {
        [theme.breakpoints.down('md')]: {
            '& *': {
                justifyContent: 'center'
            }
        }
    },
    chord: {
    },
    menu: {
        [theme.breakpoints.up('lg')]: {
            display: 'flex',
        },
        padding: 1,
        maxWidth: theme.breakpoints.values.lg,
        margin: `${theme.spacing(2)}px auto ${theme.spacing(2)}px`
    },
    footerLink: {
        color: theme.palette.grey[600]
    }
}))

const SHARE_QUERY_REGEX = /\?share=(\w+)/
const SHARE_VERSION = 1

function getInitialConfig(): {
    tonic: Note,
    mode: Mode,
    volume: number,
    attack: number
    release: number,
    sustain: number
    decay: number
    bpm: number,
    chords: Chord[]
} {
    if (SHARE_QUERY_REGEX.test(window.location.search)) {
        try {
            const config = JSON.parse(atob(SHARE_QUERY_REGEX.exec(window.location.search)![1]))
            if (config?.version === SHARE_VERSION) {
                return config
            }
        } catch (e) {
            console.error(e)
        }
    }
    return {
        tonic: 'C',
        mode: 'ionian',
        attack: 0.01,
        release: 0.1,
        sustain: 0.5,
        decay: 1,
        bpm: 120,
        volume: 50,
        chords: [
            {
                number: null,
                octave: 0,
                inversion: 0
            },
            {
                number: null,
                octave: 0,
                inversion: 0
            },
            {
                number: null,
                octave: 0,
                inversion: 0
            },
            {
                number: null,
                octave: 0,
                inversion: 0
            }

        ]
    };
}

(window as any).firestore = firestore;

const App: React.FC<Props> = () => {
    const initialConfig = getInitialConfig()
    const theme = useTheme()
    const classes = useStyles();
    const [playing, setPlaying] = useState<boolean>(false)
    const [tonic, setTonic] = useState<Note>(initialConfig.tonic)
    const [mode, setMode] = useState<Mode>(initialConfig.mode)
    const [volume, setVolume] = useState<number>(initialConfig.volume)
    const [attack, setAttack] = useState<number>(initialConfig.attack)
    const [release, setRelease] = useState<number>(initialConfig.release)
    const [sustain, setSustain] = useState<number>(initialConfig.sustain)
    const [decay, setDecay] = useState<number>(initialConfig.decay)
    const [bpm, setBpm] = useState<number>(initialConfig.bpm)
    const [chords, setChords] = useState<Chord[]>(initialConfig.chords);
    const [saveDialogOpen, setSaveDialogOpen] = useState<boolean>(false);
    const [selectProjectDialogOpen, setSelectProjectDialogOpen] = useState<boolean>(false);
    const [projectNames, setProjectNames] = useState<any[]>([])
    const { enqueueSnackbar } = useSnackbar()

    const [auth] = useAuth();

    const onLoadProject = useCallback(async (projectId) => {
        if (auth.user) {
            const project = await firestore.collection('users').doc(auth.user.uid).collection('projects').doc(projectId).get()
            const {
                tonic, mode, volume, attack, release, sustain, decay, bpm, chords, version
            } = project.data() as any;
            if (version !== SHARE_VERSION) return;
            setTonic(tonic)
            setMode(mode)
            setVolume(volume)
            setAttack(attack)
            setRelease(release)
            setSustain(sustain)
            setDecay(decay)
            setBpm(bpm)
            setChords(chords)
        }
        setSelectProjectDialogOpen(false)
    }, [auth.user])

    const updateProjectIds = useCallback(async () => {
        if (auth.user) {
            firestore.collection('users').doc(auth.user.uid).collection('projects').get().then((result) => {
                const ids = result.docs.map(doc => doc.id);
                setProjectNames(ids);
            })
        }
    }, [auth.user])

    useEffect(() => {
        if (auth.user) {
            updateProjectIds();
        }
    }, [auth.user, updateProjectIds])

    const handleShareClicked = useCallback(() => {
        const code = btoa(JSON.stringify({
            tonic, mode, volume, attack, release, sustain, decay, bpm, chords, version: SHARE_VERSION
        }));
        const url = `${window.location.protocol}//${window.location.host}?share=${code}`
        window.history.replaceState(null, 'share url', url)
        copy(url)
        enqueueSnackbar('Copied URL clipboard', { variant: 'success' })
    }, [tonic, mode, volume, attack, release, sustain, decay, bpm, chords, enqueueSnackbar])

    const projectName = useRef<HTMLInputElement>(null);
    const onSaveProject = useCallback(() => {
        const project = {
            tonic, mode, volume, attack, release, sustain, decay, bpm, chords, version: SHARE_VERSION
        }
        if (auth.user && projectName.current) {
            firestore.collection('users').doc(auth.user.uid).collection('projects').doc(projectName.current.value).set(project).then(() => {
                updateProjectIds();
                enqueueSnackbar('Saved', { variant: 'success' })
            })
            setSaveDialogOpen(false);
        }
    }, [tonic, mode, volume, attack, release, sustain, decay, bpm, chords, projectName, auth.user, updateProjectIds, enqueueSnackbar])

    const addChord = useCallback(() => {
        setChords([
            ...chords,
            {
                octave: 0,
                inversion: 0,
                number: null
            }
        ])
    }, [chords])

    const removeChord = useCallback(() => {
        setChords(chords.slice(0, chords.length - 1))
    }, [chords])

    const midiNotes = useMemo(() => {
        return chords.map(chord => {
            if (chord.number === null) return [];
            return getMidiChord(tonic, mode, chord.number, chord.octave, chord.inversion)
        })
    }, [chords, tonic, mode])

    const onPlay = useCallback(() => {
        setPlaying(!playing)
    }, [playing]);

    const [currentChord, playChord] = useMusic({
        bpm: bpm,
        playing: playing,
        volume: volume,
        midiNotes: midiNotes,
        attack, decay, sustain, release
    })

    return <>
        <Box className={classes.text} display="flex" paddingTop={4} paddingBottom={3}>
            <Box flex={1}>
                <Typography component="h1" variant="h3">Chord Playground</Typography>
            </Box>
            <Box textAlign="right" paddingRight={3}>
                <Auth />
            </Box>
        </Box>
        <Box className={classes.text} paddingBottom={3}>
            <Typography component="h3" variant="h6">What is this?</Typography>
            <Typography>
                This is a tool that you can use to experiment with <Link href="https://en.wikipedia.org/wiki/Chord_progression">diatonic chord progressions</Link>.<br />
                You can adjust various attributes that affect the quality of the chord, such as the <Link href="https://en.wikipedia.org/wiki/Tonic_(music)">scale</Link>, <Link href="https://en.wikipedia.org/wiki/Mode_(music)">mode</Link>, and <Link href="https://en.wikipedia.org/wiki/Inversion_(music)">inversion</Link>.
            </Typography>
        </Box>
        <Divider />
        <Box className={classes.menu}>
            <Box flex="2" overflow="auto" className={classes.leftMenu}>
                <Box display="flex" alignItems="center">
                    <Typography variant="h6">Tonic:</Typography>
                    <TonicSelector selectedTonic={tonic} onSelected={(tonic) => setTonic(tonic)} />
                </Box>
                <Box display="flex" alignItems="center">
                    <Typography variant="h6">Mode:</Typography>
                    <ModeSelector selectedMode={mode} onSelected={(mode => setMode(mode))} />
                </Box>
            </Box>
            <Box flex="1" overflow="auto">
                <Box display="flex" alignItems="center" justifyContent="center">
                    <Typography variant="h6">Scale:</Typography>
                    <Scale mode={mode} tonic={tonic} />
                </Box>
                <Box display="flex" alignItems="center" justifyContent="center">
                    <Button onClick={addChord} style={{ marginRight: theme.spacing(1) }} color="primary" variant="outlined">Add</Button>
                    <Button onClick={removeChord} color="secondary" variant="outlined">Remove</Button>
                </Box>
            </Box>
        </Box>
        <Box textAlign="center" overflow="auto">
            <Box className={classes.chordSelector}>{chords.map((chord, i) => <Box className={classes.chord} key={i}>
                <ChordSelector active={playing && currentChord === i} mode={mode} chord={chord.number} inversion={chord.inversion} octave={chord.octave} onSelected={chord => setChords([
                    ...chords.slice(0, i),
                    chord,
                    ...chords.slice(i + 1, chords.length)
                ])} />
                <Box textAlign="center">
                    <Button disabled={chords[i].number === null} onClick={() => playChord(midiNotes[i])} variant="outlined" color="secondary">Preview</Button>
                    <Box marginTop={1}>
                        <Typography><strong>Chord</strong>: {chord.number !== null ? getChordName(tonic, mode, chord.number) : ''}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-around" margin={2}>
                        <Typography><strong>Notes</strong>: {midiNotes[i].map(midiToNote).join(' ')}</Typography>
                        <Typography><strong>MIDI</strong>: {midiNotes[i].join(' ')}</Typography>
                    </Box>
                </Box>
            </Box>)}</Box>
        </Box>

        <Box>
            <Box textAlign="center" maxWidth={theme.breakpoints.values['md']} margin="0 auto">
                <Typography>Volume</Typography>
                <Slider
                    value={volume}
                    onChange={(e, volume) => setVolume(Number(volume))}
                />
            </Box>
            <Box textAlign="center" maxWidth={theme.breakpoints.values['md']} margin="0 auto">
                <Typography>Tempo {bpm} bpm</Typography>
                <Slider
                    value={bpm}
                    min={50}
                    max={180}
                    onChange={(e, chordLength) => setBpm(Number(chordLength))}
                />
            </Box>

            <Box textAlign="center" maxWidth={theme.breakpoints.values['md']} margin="0 auto">
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>Envelope options</AccordionSummary>
                    <AccordionDetails>
                        <Box width="100%">
                            <Typography>Attack {attack}s</Typography>
                            <Slider
                                value={attack}
                                min={0}
                                max={5}
                                step={0.1}
                                onChange={(e, value) => setAttack(Number(value))}
                            />
                            <br /><br />
                            <Typography>Decay {decay}s</Typography>
                            <Slider
                                value={decay}
                                min={0}
                                max={5}
                                step={0.1}
                                onChange={(e, value) => setDecay(Number(value))}
                            />
                            <br /><br />
                            <Typography>Sustain {sustain * 100}%</Typography>
                            <Slider
                                value={sustain * 100}
                                min={0}
                                max={100}
                                step={1}
                                onChange={(e, value) => setSustain(Number(value) / 100)}
                            />
                            <br /><br />
                            <Typography>Release {release}s</Typography>
                            <Slider
                                value={release}
                                min={0}
                                max={5}
                                step={0.1}
                                onChange={(e, value) => setRelease(Number(value))}
                            />
                        </Box>
                    </AccordionDetails>
                </Accordion>
            </Box>
        </Box>

        <Box display="flex" marginLeft="auto" marginRight="auto" marginTop={3} marginBottom={3} justifyContent="space-between" maxWidth={theme.breakpoints.values['md']}>
            <Box flexBasis="33%">
                <Button startIcon={<ShareIcon />} variant="outlined" color="primary" onClick={handleShareClicked}>Share</Button><br />
            </Box>
            <Box flexBasis="33%" textAlign="center">
                <Button variant="contained" color="primary" onClick={onPlay}>{!playing ? 'Play' : 'Pause'}</Button>
            </Box>
            <Box flexBasis="33%" textAlign="right">
                {auth.user && <>
                    <Button variant="outlined" color="primary" onClick={() => setSelectProjectDialogOpen(true)}>Load</Button>&nbsp;
                    <Button variant="contained" color="secondary" onClick={() => setSaveDialogOpen(true)}>Save</Button>
                </>}
            </Box>
        </Box>

        <Divider />
        <Box className={clsx(classes.text, classes.footerLink)} padding={3} textAlign="center">
            created by: <Link href="https://le3.io">le3.io</Link>
        </Box>

        <Dialog onClose={() => setSelectProjectDialogOpen(false)} open={selectProjectDialogOpen} fullWidth>
            <DialogTitle>Load Project</DialogTitle>
            <DialogContent>
                {projectNames.length > 0 && <>
                    <DialogContentText>Your projects:</DialogContentText>
                    <List>
                        {projectNames.map(projectName =>
                            <ListItem onClick={() => onLoadProject(projectName)} button key={projectName}>
                                <ListItemText primary={projectName} />
                            </ListItem>)}
                    </List>
                </>
                }
                {projectNames.length === 0 && <Typography>You have no projects. Save a project, and you will see it on this view.</Typography>}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setSelectProjectDialogOpen(false)} color="primary">
                    Close
                </Button>
            </DialogActions>
        </Dialog>


        <Dialog onClose={() => setSaveDialogOpen(false)} open={saveDialogOpen} fullWidth>
            <DialogTitle>Save Project</DialogTitle>
            <DialogContent>
                <DialogContentText>Please choose a name for your project.</DialogContentText>
                <TextField
                    autoFocus
                    id="name"
                    label="Name"
                    inputRef={projectName}
                    type="text"
                    fullWidth
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setSaveDialogOpen(false)} color="primary">
                    Cancel
                </Button>
                <Button onClick={onSaveProject} color="primary">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    </>
}

export default App;